import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Traps Tab/Shift+Tab within containerRef while isOpen, closes on Escape,
// and restores focus to whatever had it before opening once isOpen goes
// false (or the component unmounts). Shared by ContactInfoModal,
// VideoModal and Navbar's mobile drawer.
export default function useFocusTrap(containerRef, isOpen, onClose) {
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    triggerRef.current = document.activeElement;

    const container = containerRef.current;
    if (!container) return undefined;

    const getFocusable = () =>
      Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
        // offsetParent catches display:none (itself or an ancestor); it does
        // NOT catch visibility:hidden, which still lays out a box — checked
        // separately since Navbar's collapsed mobile dropdown relies on
        // visibility:hidden to pull its links out of tab order.
        if (el.offsetParent === null) return false;
        return getComputedStyle(el).visibility !== 'hidden';
      });

    const focusables = getFocusable();
    (focusables[0] || container).focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const items = getFocusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
      }
    };
  }, [isOpen, containerRef, onClose]);
}
